import { microToMilli as microsecondsToMillisecond } from '@core/core/constants'
import { Buffer } from 'buffer'

import {
  BINARY_FILE_HEADER_SIZE,
  DEFAULT_DATASAMPLEFORMATCODESIZE,
  DEFAULT_TRACECOUNT,
  FORMAT_CODE_OFFSET,
  NUMBER_OF_SAMPLES_IN_THIS_TRACE_OFFSET,
  NUMBER_OF_SAMPLES_PER_DATA_TRACE_FOR_ORIGINAL_FIELD_OFFSET,
  NUMBER_OF_SAMPLES_PER_DATA_TRACE_OFFSET,
  SAMPLE_INTERVAL_OF_ORIGINAL_FIELD_OFFSET,
  SAMPLE_INTERVAL_OFFSET,
  TEXTUAL_FILE_HEADER_ROW_SIZE,
  TEXTUAL_FILE_HEADER_SIZE,
  TRACE_CDP_ILINE_OFFSET,
  TRACE_CDP_X_OFFSET,
  TRACE_CDP_XLINE_OFFSET,
  TRACE_CDP_Y_OFFSET,
  TRACE_DELAYRECORDINGTIME,
  TRACE_HEADER_SIZE,
  TRACE_SOURCE_ILINE_OFFSET,
  TRACE_SOURCE_X_OFFSET,
  TRACE_SOURCE_XLINE_OFFSET,
  TRACE_SOURCE_Y_OFFSET,
} from '../../constants'
import {
  binaryHeaderTemplate,
  DataSampleFormatCodeMapping,
  IBM037Mapping,
  traceHeaderTemplate,
} from './data'
import type {
  BaseHeaderRowTemplateType,
  BaseHeaderRowType,
  BinaryHeaderRowTemplateType,
  BinaryHeaderType,
  FormatMappingKeys,
  TextualHeaderType,
  TraceHeaderRowTemplateType,
  TraceHeaderRowType,
} from './types'
import { FormatMappingKeysArray } from './types'

const convertMicrosecondsToMilliseconds = (parseResult: number): number =>
  parseResult / microsecondsToMillisecond

const isIBM037 = (buffer?: Uint8Array) => buffer?.[0] === 0xc3

export const getTextualHeader = (buffer: Uint8Array) => {
  const isIbm = isIBM037(buffer)

  const header: TextualHeaderType = { lines: [] }
  let line = []
  for (let i = 0; i < TEXTUAL_FILE_HEADER_SIZE; i++) {
    const byte = buffer[i]
    line.push(isIbm ? IBM037Mapping.get(byte) : String.fromCharCode(byte))
    if ((i + 1) % TEXTUAL_FILE_HEADER_ROW_SIZE === 0) {
      header.lines.push(line.join(''))
      line = []
    }
  }

  return header
}

const readUint16 = (array: ArrayBuffer, byteOffset: number) =>
  Buffer.from(array, byteOffset).readUInt16BE(0)

const readInt16 = (array: ArrayBuffer, byteOffset: number) =>
  Buffer.from(array, byteOffset).readInt16BE(0)

const readInt32 = (array: ArrayBuffer, byteOffset: number) =>
  Buffer.from(array, byteOffset).readInt32BE(0)

function getHeader<TTemplate extends BaseHeaderRowTemplateType>(
  array: ArrayBuffer,
  byteOffset: number,
  length: number,
  headerTemplate: TTemplate[],
) {
  const buf = Buffer.from(array, byteOffset, length)
  const getValue = (item: TTemplate) => {
    const valueProcessor = item.postProcess ?? (<T>(item: T) => item)
    switch (item.type) {
      case 'int16':
        return valueProcessor(buf.readInt16BE(item.position))
      case 'uint16':
        return valueProcessor(buf.readUint16BE(item.position))
      case 'int32':
        return valueProcessor(buf.readInt32BE(item.position))
      case 'uint32':
        return valueProcessor(buf.readUint32BE(item.position))
    }
  }

  const rows: BaseHeaderRowType[] = headerTemplate.map((template, index) => ({
    id: index.toString(),
    position: template.position,
    description: template.description,
    value: getValue(template),
  }))

  return rows
}

export const getBinaryHeader = (array: ArrayBuffer): BinaryHeaderType =>
  getHeader<BinaryHeaderRowTemplateType>(
    array,
    TEXTUAL_FILE_HEADER_SIZE,
    BINARY_FILE_HEADER_SIZE,
    binaryHeaderTemplate,
  )
export const getTraceHeaders = (
  array: ArrayBuffer,
  dataSampleFormatCodeSize: number,
  traceCount: number = DEFAULT_TRACECOUNT,
) => {
  const traceHeaders: TraceHeaderRowType[][] = []
  let pos = 0
  for (let i = 0; i < traceCount; i++) {
    const traceHeader = getHeader<TraceHeaderRowTemplateType>(
      array,
      pos,
      TRACE_HEADER_SIZE,
      traceHeaderTemplate,
    )
    const samplesNumber = readUint16(array, pos + NUMBER_OF_SAMPLES_IN_THIS_TRACE_OFFSET)
    pos += samplesNumber * dataSampleFormatCodeSize + TRACE_HEADER_SIZE

    traceHeaders.push(traceHeader)
  }

  return traceHeaders
}

export const getDataSampleFormatCodeSize = (array: ArrayBuffer) =>
  DataSampleFormatCodeMapping.get(
    readUint16(
      array,
      NUMBER_OF_SAMPLES_PER_DATA_TRACE_FOR_ORIGINAL_FIELD_OFFSET + TEXTUAL_FILE_HEADER_SIZE,
    ),
  ) ?? DEFAULT_DATASAMPLEFORMATCODESIZE

const getNumberOfSamplesPerDataTrace = (array: ArrayBuffer) =>
  readUint16(array, SAMPLE_INTERVAL_OF_ORIGINAL_FIELD_OFFSET + TEXTUAL_FILE_HEADER_SIZE)

export const getDataTraceSize = (array: ArrayBuffer, traceCount: number = DEFAULT_TRACECOUNT) => {
  const numberOfSamplesPerDataTrace = getNumberOfSamplesPerDataTrace(array)
  const dataSampleFormatCodeSize = getDataSampleFormatCodeSize(array)
  return (TRACE_HEADER_SIZE + numberOfSamplesPerDataTrace * dataSampleFormatCodeSize) * traceCount
}

export const getSamplingStep = (array: ArrayBuffer) =>
  convertMicrosecondsToMilliseconds(
    readUint16(array, SAMPLE_INTERVAL_OFFSET + TEXTUAL_FILE_HEADER_SIZE),
  )

export const getNumberOfSamples = (array: ArrayBuffer) =>
  readUint16(array, NUMBER_OF_SAMPLES_PER_DATA_TRACE_OFFSET + TEXTUAL_FILE_HEADER_SIZE)

export const getFormatCode = (array: ArrayBuffer): FormatMappingKeys | undefined => {
  const code = readUint16(array, FORMAT_CODE_OFFSET + TEXTUAL_FILE_HEADER_SIZE)
  return FormatMappingKeysArray.find(key => key === code)
}

export const getHeaderCoords = (
  array: ArrayBuffer,
): { x: number; y: number; iline: number; xline: number } | undefined => {
  const coords: { x: number; y: number; iline: number; xline: number } = {
    x: 0,
    y: 0,
    iline: 0,
    xline: 0,
  }
  if (readInt32(array, TRACE_SOURCE_X_OFFSET) && readInt32(array, TRACE_SOURCE_Y_OFFSET)) {
    coords.x = TRACE_SOURCE_X_OFFSET
    coords.y = TRACE_SOURCE_Y_OFFSET
    coords.iline = TRACE_SOURCE_ILINE_OFFSET
    coords.xline = TRACE_SOURCE_XLINE_OFFSET
  } else if (readInt32(array, TRACE_CDP_X_OFFSET) && readInt32(array, TRACE_CDP_Y_OFFSET)) {
    coords.x = TRACE_CDP_X_OFFSET
    coords.y = TRACE_CDP_Y_OFFSET
    coords.iline = TRACE_CDP_ILINE_OFFSET
    coords.xline = TRACE_CDP_XLINE_OFFSET
  }

  return coords.x && coords.y ? coords : undefined
}

export const getDelayRecordingTime = (array: ArrayBuffer) =>
  readInt16(array, TRACE_DELAYRECORDINGTIME)
